/**
 * The in-place refresh that replaced guardSelect's hard page reload.
 *
 * ★ WHY THIS FILE EXISTS. Legacy `cascade_warning.js:60-77` answered a
 * non-empty Checkpoint-4 cascade with `window.location.reload()`, and said in
 * its own comment why:
 *
 *   "Targeted per-component refresh would be ideal, but every affected
 *    component (coil, ERW, PHX, gas heater, evap pack, UDA) has its own
 *    Backbone view and model collection. Wiring 'refresh this component' per
 *    type is a bigger job; for now, force a page reload…"
 *
 * i.e. the reload was never the intended UX — it was the cheap stand-in for
 * work Backbone made expensive. React does not make it expensive: every one of
 * those components reads its row from an RTK Query cache keyed by id. The
 * reload cost the engineer the whole board (canvas rebuild, every form
 * remounted, the selected section, the scroll position, any unsaved typing)
 * and ~11 GETs per tunnel, to carry a handful of changed rows.
 *
 * WHAT HAS TO BE RE-READ. Two separate writes land in the same `/select`
 * request, and both are invisible to the response the caller gets back:
 *
 *  1. THE CASCADE's own writes (engineering/airstream_propagation.py, via
 *     api/rest_ahu/cascade_helpers.py):
 *      - the host SECTION's stored entering/leaving air conditions, for every
 *        component the walk steps through (`section.save()` in
 *        _flag_coil_rerate, in the filter/eliminator/silencer branch and in
 *        the fan branch);
 *      - `needs_rerate_summer` / `needs_rerate_winter` on coils and on ERW/PHX
 *        bridges (_flag_coil_rerate / _flag_bridge_rerate);
 *      - the gas heater's / evap pack's / electric-heater UDA's own entering
 *        and leaving values, zeroed or recomputed (_zero_* / _recompute_*);
 *      - a humidifier UDA's entering (_sync_humidifier_entering).
 *  2. THE SELECT's own RESIZE. `partial_update` runs before the cascade in the
 *     same request, and `AHUSection.save()` re-derives `length` from
 *     `required_length` when auto-adjust is on — so the Select that triggered
 *     the cascade may also have made its host section longer or shorter.
 *     Nothing in the `/select` RESPONSE carries that (useAhuBoardSyncdata.ts's
 *     header says so), so the section re-read below has to do what the
 *     `ahusection` syncdata handler does with a length change —
 *     `followSectionLengthChange`: `recalcTunnelLength`, the
 *     `section:length:changed` event (the fan's VFD position listens) and a lug
 *     re-read. Without it this refetch could land ahead of the websocket echo,
 *     leave the echo with nothing to diff, and strand `tunnel.length` and the
 *     lug placements at their pre-Select values — geometry the page reload got
 *     right.
 *
 *     ★★ THE BASELINE FOR "DID LENGTH MOVE" IS TAKEN BEFORE THE POST, by
 *     `snapshotSectionLengths()` in `guardSelect`, and NOT re-read here. It has
 *     to be: on the fan path `onCommit` → `commitFanChain` → `persistSectionDraft`
 *     PATCHes the section, and `patchAhuSection` merges the server's FULL row —
 *     `length` included, already re-derived by the Select — into the
 *     section-list cache. Reading the baseline inside this function (which
 *     waits for exactly that PATCH to drain first) therefore read the NEW
 *     length as if it were the old one, `lengthMoved` came out false, and the
 *     lug/VFD follow-through never fired. Same hole for any selector that
 *     PATCHes its section inside the commit chain — coil, gas heater, evap pack.
 *
 * TWO THINGS IT DELIBERATELY DOES NOT DO:
 *
 * 1. It does not fetch a row whose form is not mounted. `invalidateTags`
 *    re-GETs only the cache entries that have a live subscriber and marks the
 *    rest stale. ⚠ That is NOT the same as "costs nothing": the BOARD's own
 *    per-component detail queries are subscribed for the board's lifetime (the
 *    bootstrap feed and ErrorManager subscribe to every coil), so invalidating
 *    `coil-<id>` does issue a GET — and a GET on `ahu/coils/<id>` WRITES (see
 *    the memory on a coil retrieve calling `coil.save()`). The page reload
 *    re-GOT all of them too, so this is not more traffic than the base; what
 *    it must not do is fetch the SAME url twice, which is why a component with
 *    a mounted form is fetched once and the result written into both caches.
 * 2. It does not re-read the psychrometric chart, which is where a cascade's
 *    `needs_rerate` flags are actually rendered. That endpoint WRITES on GET
 *    (psychrometricApi.ts header) and legacy fetches it at exactly two moments,
 *    dialog-open being one — so an open chart is refreshed by reopening it,
 *    exactly as before.
 */

import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { ahuComponentFormsApi } from 'app/services/ahuComponentFormsApi';
import { store } from 'app/store';
import type { RootState } from 'app/store';
import { waitUntilIdle } from 'app/services/ahuBoardApi';
import { holdCanvasRedrawBatchFor } from '../three/canvasRedrawScheduler';
import { getRegisteredModel } from './autoSaveModel';
import { anySectionPatchPending } from './sectionPatchQueue';
import { COMPONENT_PLURALS, classIdFor, type ComponentTypeName } from './componentFormTypes';

/** One entry of the cascade impact report — airstream_propagation.py's
 * `report.append({'kind', 'id', 'action', 'label'})`. */
export interface CascadeImpact {
  kind?: string | null;
  id?: number | null;
  action?: string | null;
  label?: string | null;
}

/**
 * The report's `kind` vocabulary → this board's component type. The two agree
 * except for `plate_hex`, the engine's name for what the form layer calls
 * `plate_heat_exchanger`.
 */
const CASCADE_KIND_TO_TYPE: Record<string, ComponentTypeName> = {
  coil: 'coil',
  fan: 'fan',
  filter: 'filter',
  eliminator: 'eliminator',
  silencer: 'silencer',
  evap_pack: 'evap_pack',
  gas_heater: 'gas_heater',
  energy_wheel: 'energy_wheel',
  plate_hex: 'plate_heat_exchanger',
};

/** The two report kinds that are AHUUserDefinedAccessory rows rather than
 * components (cascade_helpers.snapshot_component gives each its own kind), so
 * they live in the ACCESSORY caches and config tables instead. */
const UDA_KINDS = new Set(['electric_heater', 'humidifier']);
const UDA_PLURAL = 'user_defined_accessories';
/** accessoryFormConfigs.ts:823. */
const UDA_CLASS_ID = 'model.accessory.user_defined_accessory';

/**
 * The BOARD's own per-component detail queries (ahuBoardApi) — the four kinds
 * that have one. They read the SAME url as the form layer's
 * `getComponentDetail` for that plural, under a second cache key, which is why
 * the mounted case writes the one fetched row into both rather than asking
 * twice.
 */
const BOARD_DETAIL: Record<
  string,
  { tagId: (id: number) => string; write: (id: number, row: Record<string, unknown>) => unknown }
> = {
  coil: {
    tagId: (id) => `coil-${id}`,
    write: (id, row) =>
      ahuBoardApi.util.updateQueryData('getAhuCoilDetail', id, (draft) => {
        Object.assign(draft, row);
      }),
  },
  fan: {
    tagId: (id) => `fan-${id}`,
    write: (id, row) =>
      ahuBoardApi.util.updateQueryData('getAhuFanDetail', id, (draft) => {
        Object.assign(draft, row);
      }),
  },
  evap_pack: {
    tagId: (id) => `evap-${id}`,
    write: (id, row) =>
      ahuBoardApi.util.updateQueryData('getAhuEvapPackDetail', id, (draft) => {
        Object.assign(draft, row);
      }),
  },
  gas_heater: {
    tagId: (id) => `gas-heater-${id}`,
    write: (id, row) =>
      ahuBoardApi.util.updateQueryData('getAhuGasHeaterDetail', id, (draft) => {
        Object.assign(draft, row);
      }),
  },
};

/** `{kind, id}` pairs, de-duplicated — a bridge is reported once per chain. */
function impactedRows(
  cascade: { upstream?: unknown[]; downstream?: unknown[] } | null | undefined
): Array<{ kind: string; id: number }> {
  const seen = new Set<string>();
  const out: Array<{ kind: string; id: number }> = [];
  for (const entry of [...(cascade?.upstream ?? []), ...(cascade?.downstream ?? [])]) {
    const { kind, id } = (entry ?? {}) as CascadeImpact;
    const numId = Number(id);
    if (!kind || !Number.isFinite(numId)) continue;
    if (!(kind in CASCADE_KIND_TO_TYPE) && !UDA_KINDS.has(kind)) continue;
    const key = `${kind}:${numId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ kind, id: numId });
  }
  return out;
}

/** sectionId → length, per tunnel: what the board held before the Select. */
export type SectionLengthSnapshot = Map<number, Map<number, unknown>>;

/**
 * The pre-POST length baseline — see the header. SYNCHRONOUS on purpose:
 * `guardSelect` calls it before it issues the request, which is the last
 * moment at which the cache is guaranteed to predate every write the Select
 * makes.
 */
export function snapshotSectionLengths(): SectionLengthSnapshot {
  const { getState } = store;
  const snapshot: SectionLengthSnapshot = new Map();
  for (const tunnelId of ahuBoardApi.util.selectCachedArgsForQuery(
    getState(),
    'getTunnelSectionList'
  )) {
    const rows = ahuBoardApi.endpoints.getTunnelSectionList.select(tunnelId)(
      getState() as RootState
    ).data;
    if (!rows) continue;
    snapshot.set(tunnelId, new Map(rows.map((row) => [row.id, row.length])));
  }
  return snapshot;
}

/**
 * Re-read every tunnel's section list, and follow a length change through.
 *
 * TWO ORDERING HAZARDS, both of which a plain `forceRefetch` walks into and
 * neither of which guardSelect's 150 ms timer actually rules out:
 *
 *  - the CALLER's own writes. `onCommit` starts the commit chain (for the fan,
 *    `queueSectionPatch`) and nothing awaits it, so a GET fired 150 ms later
 *    can read the row before that PATCH and land after it — leaving the board
 *    on pre-Select air conditions, with the syncdata echo unable to repair it
 *    (it skips sections with a pending patch). So wait for the section-patch
 *    queue to drain first.
 *  - RTK Query's own collapse. The query thunk refuses to start while a read
 *    for the same key is in flight and `initiate` then resolves with THAT
 *    request's result, which may predate the write — the race
 *    `refetchTunnelListAfterWrite` documents at length. So wait for the entry
 *    to be idle before forcing the read.
 */
async function refreshSections(baseline: SectionLengthSnapshot): Promise<void> {
  const { dispatch, getState } = store;
  const tunnelIds = ahuBoardApi.util.selectCachedArgsForQuery(
    getState(),
    'getTunnelSectionList'
  );
  if (!tunnelIds.length) return;

  await waitUntilIdle(anySectionPatchPending);

  // The board's AHU id, from the `getAhuUnit` cache args — exactly one entry
  // per board mount (the same read `boardAhuId` does; inlined so this module
  // does not import commitAirConditions, which imports guardSelect, which
  // imports this one).
  const ahuId =
    (ahuBoardApi.util.selectCachedArgsForQuery(getState(), 'getAhuUnit')[0] as
      | number
      | undefined) ?? null;
  // Dynamic so the static import graph stays acyclic: boardActions reaches
  // guardSelect through useAccessoryForm, and guardSelect imports this module.
  // Already in this chunk, so it resolves without a fetch.
  const { followSectionLengthChange } = await import('../boardActions');

  await Promise.all(
    tunnelIds.map(async (tunnelId) => {
      const select = ahuBoardApi.endpoints.getTunnelSectionList.select(tunnelId);
      await waitUntilIdle(() => select(getState() as RootState).status === 'pending');

      const req = dispatch(
        ahuBoardApi.endpoints.getTunnelSectionList.initiate(tunnelId, { forceRefetch: true })
      );
      // One canvas pass for the whole refresh rather than one per tunnel.
      holdCanvasRedrawBatchFor(req);
      const after = await req
        .unwrap()
        .catch(() => undefined)
        .finally(() => req.unsubscribe());
      if (!after) return;

      // Against the PRE-POST snapshot, never a re-read (see the header): the
      // Select's own commit chain may already have merged the new length into
      // this cache by now. A tunnel the snapshot does not know (loaded since)
      // counts as unchanged rather than as changed-from-nothing.
      const was = baseline.get(tunnelId);
      const lengthMoved =
        !!was &&
        after.some(
          (row) => was.has(row.id) && String(row.length ?? '') !== String(was.get(row.id) ?? '')
        );
      if (!lengthMoved) return;
      await dispatch(followSectionLengthChange(ahuId, tunnelId));
    })
  );
}

/**
 * Re-read what a non-empty cascade changed, without navigating.
 *
 * Safe to call with an absent or empty cascade (no-op) and off the board: the
 * header's independent rating popup holds none of these caches, so both passes
 * find nothing and nothing throws.
 */
export function refreshAfterCascade(
  cascade: { upstream?: unknown[]; downstream?: unknown[] } | null | undefined,
  /** The pre-POST section lengths (see the header). Absent → the caller took
   * none, and a resize simply does not follow through. */
  baseline: SectionLengthSnapshot = new Map()
): void {
  const rows = impactedRows(cascade);
  if (!rows.length) return;
  const { dispatch } = store;

  // ---- 1. the SECTION rows (and the Select's own resize behind them) ------
  void refreshSections(baseline);

  // ---- 2. the COMPONENT rows ---------------------------------------------
  for (const { kind, id } of rows) {
    const isUda = UDA_KINDS.has(kind);
    const plural = isUda ? UDA_PLURAL : COMPONENT_PLURALS[CASCADE_KIND_TO_TYPE[kind]];
    const classID = isUda ? UDA_CLASS_ID : classIdFor(CASCADE_KIND_TO_TYPE[kind]);
    const board = isUda ? undefined : BOARD_DETAIL[kind];

    // A registered model means a form is MOUNTED on this component — both
    // `useComponentForm` and `useAccessoryForm` build the model and subscribe
    // to the row together — and it also means the row's values live somewhere
    // no cache write reaches: `useComponentForm` keys its FormModel on
    // `[row.id, !detail]` precisely so a refetch cannot rebuild it, and its own
    // comment says the listeners "set the fresh fields manually (xhr echo)".
    const model = getRegisteredModel(classID, id);
    // Their own write is still in hand — either on the wire (`pennding`) or
    // queued in the §49.2 debounce window. Its success handler writes the
    // authoritative row, and a GET issued now would read the component
    // mid-write; merging the stored row over a field they are still typing in
    // would also flicker it back (hasUnsavedChanges — the syncdata listener's
    // guard, widened to the window before `pennding` flips).
    if (model && (model.pennding || model.hasUnsavedChanges())) continue;

    if (!model) {
      // Nothing is showing it: mark the rows stale and let whoever subscribes
      // re-read. The form row has no subscriber so this is free; the board's
      // own query does have one (see the first of the two deviations above),
      // so it re-GETs once.
      dispatch(
        ahuBoardApi.util.invalidateTags([
          isUda
            ? { type: 'AhuAccessories' as const, id: `${UDA_PLURAL}-${id}` }
            : { type: 'AhuComponents' as const, id: `${plural}-${id}` },
          ...(board ? [{ type: 'AhuComponents' as const, id: board.tagId(id) }] : []),
        ])
      );
      continue;
    }

    // On screen: ONE fetch, feeding the form's cache, the form's model and —
    // because the board's own detail query reads the SAME url under another
    // key — that cache too, instead of a second writing GET of it.
    const req = dispatch(
      isUda
        ? ahuBoardApi.endpoints.getAccessoryDetail.initiate(
            { plural, id },
            { subscribe: false, forceRefetch: true }
          )
        : ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
            { plural, id },
            { subscribe: false, forceRefetch: true }
          )
    );
    void req
      .unwrap()
      .then((fresh) => {
        const row = fresh as Record<string, unknown>;
        if (board) dispatch(board.write(id, row) as Parameters<typeof dispatch>[0]);
        // Re-read the guard: the user may have started a save during the GET.
        // `__from_back_end` is the §19.2 echo flag — merge, never save.
        const live = getRegisteredModel(classID, id);
        if (live && !live.pennding && !live.hasUnsavedChanges()) {
          live.set(row, { __from_back_end: true });
        }
      })
      .catch(() => undefined);
  }
}
